import * as React from 'react'
import {
    ContextualMenu,
    ContextualMenuItemType,
    DetailsListLayoutMode,
    IColumn,
    IContextualMenuItem,
    IDetailsListProps,
    IDetailsRowProps,
    ISelection,
    ISelectionOptions,
    MarqueeSelection,
    Selection,
    SelectionMode,
    ShimmeredDetailsList,
} from '@fluentui/react'
import { IRefObject, IRenderFunction } from '@fluentui/utilities'

export interface ICoreDetailsListProps<I> extends IDetailsListProps {
    ref?: IRefObject<DetailsListAbstract<I>>
    filters?: ICoreDetailsListFilter[]
    // The filter settings.
    settings?: ICoreDetailsListSettings
    isSortable?: boolean
    selectionOptions?: Partial<ISelectionOptions>
    contextualMenuItems?: IDynamicContextualMenuItem[][]
    getContextualMenuTitle?: (item: I, items: I[]) => string
    // Returns the new selection.
    onSelectItem?: (item: I, selection: ISelection) => void
    sortItems?: (column: ICoreColumn, items: I[]) => I[]
    onSortClicked?: (column: IColumn) => void
    selection?: ISelection
    items: I[]
    emptyDescription: string
}

export interface ICoreDetailsListState<I> {
    columns: IColumn[]
    items: I[]
    activeColumn?: IColumn
    selection: ISelection // The current Selection object.
    activeItem: I // The item that triggered an event.
    selectedItems: I[] // The full selection, which can be different from the selection object.
    isContextualMenuOpen: boolean // A selection does not mean that the contextual menu should open.
    event: Event // The current event that is the target of the context menu.
    enableShimmer: boolean
}

export interface IDynamicContextualMenuItem<I extends ICoreDetailsListItem = ICoreDetailsListItem>
    extends IContextualMenuItem {
    access?: (item: I, selectedItems: I[]) => boolean // If the menu item should be enabled.
    click?: (item: I, selectedItems: I[]) => void // If the menu item should be enabled.
    filter?: (item: I, selectedItems: I[]) => boolean // If the menu item should be shown.
}

export interface ICoreDetailsListFilter<
    I extends Object = Object,
    S extends ICoreDetailsListSettings = ICoreDetailsListSettings,
> {
    (items: I[], settings: S): I[]
}

export interface ICoreDetailsListItem {
    [key: string]: any
}

export interface ICoreDetailsListSettings {
    query?: string // The query when doing a textual search.
    minQueryLength?: number // The minimum length of the search query if we have a search box.
    matchColumns?: string[] // The column names to search in.
    [key: string]: any
}

export interface ICoreColumn<I extends ICoreDetailsListItem = ICoreDetailsListItem> extends IColumn {
    filter?: () => boolean
    isSortable?: boolean
    sortItems?: (column: ICoreColumn<I>, items: I[]) => I[]
}

export default class DetailsListAbstract<I extends ICoreDetailsListItem = ICoreDetailsListItem> extends React.Component<
    ICoreDetailsListProps<I>,
    ICoreDetailsListState<I>
> {
    public state: ICoreDetailsListState<I> = {
        columns: [],
        items: null,
        isContextualMenuOpen: false,
        selection: new Selection({
            onSelectionChanged: () => this.updateSelectedState(),
            getKey: item => this.getKey(item),
            selectionMode:
                this.props.selectionMode === SelectionMode.multiple ? SelectionMode.multiple : SelectionMode.single,
            ...(this.props.selectionOptions || {}),
        }),
        activeItem: null,
        selectedItems: [],
        event: null,
        enableShimmer: true,
    }

    public static getDerivedStateFromProps<I extends ICoreDetailsListItem = ICoreDetailsListItem>(
        nextProps: Readonly<ICoreDetailsListProps<I>>,
        prevState: Readonly<ICoreDetailsListState<I>>,
    ): ICoreDetailsListState<I> {
        // Create a local copy of the items and columns, so this object's props remain unaffected.
        let items: I[] = nextProps.items ? [...nextProps.items] : null

        // Filters come before sorting, because the items may get enriched.
        if (items) {
            nextProps.filters?.forEach?.((filter: ICoreDetailsListFilter) => {
                items = filter.call(this, items, nextProps.settings || {})
            })
        }

        const activeColumn = prevState.activeColumn || nextProps.columns.find((column: IColumn) => column.isSorted)
        const columns: ICoreColumn<I>[] = [...(nextProps.columns || [])].map((column: ICoreColumn<I>) => {
            if (activeColumn?.key === column.key) {
                const headers: ICoreColumn<I>[] = nextProps.columns.filter(
                    (_column: ICoreColumn<I>) => _column.isRowHeader && _column.key !== column.key,
                )

                column = {
                    ...column,
                    isSorted: true,
                    isSortedDescending: activeColumn?.isSortedDescending,
                }

                if (items) {
                    ;[...headers, column].forEach((_column: ICoreColumn<I>) => {
                        // Sort the items, if this column is marked as the active one.
                        if (nextProps.sortItems) {
                            items = nextProps.sortItems(_column, items)
                        } else if (_column.sortItems) {
                            items = _column.sortItems(_column, items)
                        } else {
                            items = items.slice().sort(DetailsListAbstract.columnSort(_column))
                        }
                    })
                }
            } else {
                column = { ...column, isSorted: false, isSortedDescending: true }
            }
            return column
        })

        return {
            ...prevState,
            selection: nextProps.selection || prevState.selection,
            columns,
            activeColumn,
            // If there are no items in the current selection, add a dummy item to trigger an error message.
            items: items !== null ? (items.length !== 0 ? items : [DetailsListAbstract.noItem as I]) : null,
            enableShimmer: items === null,
        }
    }

    public static columnSort(column: ICoreColumn): (a: ICoreDetailsListItem, b: ICoreDetailsListItem) => number {
        const descending: boolean = column.isSortedDescending || false
        return (itemA: ICoreDetailsListItem, itemB: ICoreDetailsListItem) => {
            let a = column.data ? column.data(itemA) : itemA[column.fieldName] || itemA[column.key] || null
            let b = column.data ? column.data(itemB) : itemB[column.fieldName] || itemB[column.key] || null
            if (a && typeof a === 'string') {
                a = a.toLowerCase()
            }
            if (b && typeof b === 'string') {
                b = b.toLowerCase()
            }

            if (a < b || (a === null && b !== null)) {
                return descending ? 1 : -1
            }
            if (a > b || (a !== null && b === null)) {
                return descending ? -1 : 1
            }
            return 0
        }
    }

    public static noItem: ICoreDetailsListItem = { key: 'no-item' }

    public render(): React.ReactNode {
        return (
            <MarqueeSelection
                selection={this.state.selection}
                isEnabled={this.state.selection.mode === SelectionMode.multiple}
                className="clear"
                isDraggingConstrainedToRoot
            >
                <ShimmeredDetailsList
                    compact
                    layoutMode={DetailsListLayoutMode.justified}
                    onShouldVirtualize={() => true}
                    usePageCache
                    useReducedRowRenderer={false}
                    setKey={this.props.setKey || 'id'}
                    selectionMode={SelectionMode.none}
                    onItemContextMenu={(item: I, _index: number, event: Event) => {
                        // Stop the default context menu from popping up.
                        event.preventDefault()
                        if (!(event instanceof Event)) {
                            // Persist the event data if the event is a SyntheticEvent, because it's omitted by default.
                            ;(event as React.SyntheticEvent).persist()
                        }
                        this.setState(
                            {
                                event: event,
                                activeItem: item,
                                selectedItems: this.state.selectedItems,
                                isContextualMenuOpen: true,
                            },
                            () => this.props.onSelectItem && this.props.onSelectItem(item, this.state.selection),
                        )
                    }}
                    {...this.props}
                    items={this.state.items || []}
                    selection={this.state.selection}
                    enableShimmer={this.state.enableShimmer}
                    onRenderRow={(props: IDetailsRowProps, render: IRenderFunction<IDetailsRowProps>) => {
                        props.className = this.getSelectedItems().includes(props.item)
                            ? 'ms-bgColor-neutralLighter'
                            : ''
                        // An empty item is provided by applyFilters() when no items are set.
                        return props.item.key === DetailsListAbstract.noItem.key ? (
                            <div className="ms-font-m ms-textAlignCenter" style={{ padding: '6px' }} key="no-item">
                                <em>{this.props.emptyDescription}</em>
                            </div>
                        ) : this.props.onRenderRow ? (
                            this.props.onRenderRow(props, render)
                        ) : (
                            render(props)
                        )
                    }}
                    columns={this.state.columns.map((column: ICoreColumn) => {
                        if (column.hasOwnProperty('onColumnClick')) {
                            return column
                        }
                        const onColumnClick = (_event: React.MouseEvent<HTMLElement>, current: IColumn) => {
                            this.setState({
                                activeColumn: {
                                    ...current,
                                    isSortedDescending: !current.isSortedDescending,
                                },
                            })
                            this.props.onSortClicked?.(current)
                        }

                        return {
                            ...column,
                            onColumnClick: column.isSortable ? onColumnClick : null,
                        }
                    })}
                />
                {this.renderContextualMenu()}
            </MarqueeSelection>
        )
    }

    public getActiveItem(): I {
        return this.state.activeItem
    }

    public getSelectedItems(): I[] {
        return this.state.selectedItems as I[]
    }

    private getKey(item: ICoreDetailsListItem): string {
        return item && item[this.props.setKey || 'id']
    }

    private updateSelectedState(): void {
        this.setState({
            event: null,
            activeItem: null,
            isContextualMenuOpen: false,
            // Only keep selectable items.
            selectedItems: this.state.selection.getSelection() as I[],
        })
    }

    private renderContextualMenu(): React.ReactNode {
        const activeItem: I = this.getActiveItem()
        const selectedItems: I[] = this.getSelectedItems() || []

        if (!this.state.isContextualMenuOpen) {
            return <></>
        }

        const title: string = this.props.getContextualMenuTitle
            ? this.props.getContextualMenuTitle(activeItem, selectedItems)
            : 'Eigenschappen'

        const items: IContextualMenuItem[] = (this.props.contextualMenuItems || [])
            .map((group: IDynamicContextualMenuItem[]) =>
                group.filter((menuItem: IDynamicContextualMenuItem) =>
                    menuItem.filter ? menuItem.filter(activeItem, selectedItems) : true,
                ),
            )
            .filter((group: IDynamicContextualMenuItem[]) => group.length > 0)
            .map((group: IDynamicContextualMenuItem[]) =>
                group.map((menuItem: IDynamicContextualMenuItem) => ({
                    onClick: () => menuItem.click?.(activeItem, selectedItems),
                    ...menuItem,
                    disabled: menuItem.access ? !menuItem.access(activeItem, selectedItems) : false,
                })),
            )
            .flatMap((group: IDynamicContextualMenuItem[], index: number, items: []) =>
                items.length !== 0
                    ? [
                          {
                              key: `divider-${index}`,
                              itemType: ContextualMenuItemType.Divider,
                          },
                          ...group,
                      ]
                    : group,
            )

        return (
            <ContextualMenu
                target={this.state.event as MouseEvent}
                shouldFocusOnContainer={false}
                shouldFocusOnMount={false}
                onRestoreFocus={() => {
                    // Left blank intentionally to not restore focus to parent container.
                    // @todo control focus for document rename input box from here.
                }}
                title={title}
                items={items}
                onDismiss={() => {
                    // If this is not a multi select, remove all selected items.
                    if (!this.props.selectionMode) {
                        this.state.selection.setAllSelected(false)
                    }
                    this.updateSelectedState()
                }}
            />
        )
    }
}
